import {
    ThemeProvider,
    Box,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Typography,
    IconButton,
    InputAdornment,
    lighten,
    Drawer,
    useMediaQuery,
    ListItemButton,
    Checkbox,
} from "@mui/material";
import { AddBox, Close as CloseIcon, Menu, Search } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../store";
import { fetchChapters as fetchChaptersHelper } from "../utils/helpers/chapterHelper";
import { useCallback, useState, useEffect, useMemo } from "react";
import Theme from "../utils/theme";
import Sidebar from "../components/Sidebar";
import axiosService from "../utils/axios";
import ThumbnailGrid from "../components/ThumbnailGrid";
import LibraryRecipe from "../components/LibraryRecipe";
import CustomButton from "../components/default/CustomButton";
import { Delete, Close } from "@mui/icons-material";
import { addChapterIngredientTransform, fetchRecipes } from "../utils/helpers/recipeHelper";
import type { ISavedRecipes } from "../types/recipe";
import type { IChapter } from "../types/chapter";
import EditRecipe from "../components/EditRecipe";
import { reset as resetEditData } from "../store/slices/editData";
import { useNavigate } from "react-router-dom";
import LoadingWithTip from "../components/LoadingWithTip";
import { scrollbarCss } from "../utils/themeScrollbar";

const Library = () => {
    const navigate = useNavigate();
    const authState = useSelector((state: RootState) => state.auth);
    const isGuest = !authState.account;
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);
    const editData = useSelector((state: RootState) => state.editData);

    const dispatch = useDispatch();

    const [chapters, setChapters] = useState<IChapter[]>([]);
    const [savedRecipes, setSavedRecipes] = useState<ISavedRecipes[]>([]);
    const [favoriteRecipes, setFavoriteRecipes] = useState<ISavedRecipes[]>([]);
    const [showFavorites, setShowFavorites] = useState(false);
    const [selectedTab, setSelectedTab] = useState(-1);
    const [sidebarTabs, setSidebarTabs] = useState<Array<{ label: string }>>([]);
    const [error, setError] = useState("");
    const [currentRecipe, setCurrentRecipe] = useState<ISavedRecipes | null>(null);
    const [newChapterNew, setNewChapterName] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [dialogMode, setDialogMode] = useState<"chapter" | "recipe">("chapter");
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [saveToChapter, setSaveToChapter] = useState(false);

    const mdUp = useMediaQuery(theme.breakpoints.up("md"));
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        if (isGuest) {
            navigate("/login");
            return;
        }

        const t = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 250);
        return () => clearTimeout(t);
    }, [query, navigate, isGuest]);

    const filteredRecipes = useMemo(() => {
        const joinedRecipes = [...new Map([...favoriteRecipes, ...savedRecipes].map((item) => [item._id, item])).values()];

        const baseArray = showFavorites
            ? favoriteRecipes
            : chapters[selectedTab]
            ? joinedRecipes.filter((recipe) => chapters[selectedTab]["recipes"].includes(recipe._id))
            : joinedRecipes;

        const reversedBaseArray = [...baseArray].reverse();

        if (!debouncedQuery) return reversedBaseArray;

        return reversedBaseArray.filter((r: any) => {
            const name = (r?.name ?? "").toLowerCase();
            const user = (r?.user?.username ?? r?.user ?? "").toLowerCase();
            const tags = Array.isArray(r?.tags) ? r.tags.join(" ").toLowerCase() : "";
            return name.includes(debouncedQuery) || user.includes(debouncedQuery) || tags.includes(debouncedQuery);
        });
    }, [showFavorites, favoriteRecipes, chapters, selectedTab, savedRecipes, debouncedQuery]);

    const fetchChapters = useCallback(async () => {
        try {
            const res = await fetchChaptersHelper();
            const newChapters = res.data.body.chapters;
            setChapters(newChapters);

            const sidebarTabsMap = newChapters.map((chapter: IChapter) => {
                return { label: chapter.name };
            });

            setSidebarTabs(sidebarTabsMap);

            return newChapters as IChapter[];
        } catch (error) {
            setError("Failed to retrieve chapters");
        }
    }, []);

    const fetchSavedRecipes = useCallback(async () => {
        setLoading(true);

        try {
            fetchRecipes()
                .then((res) => {
                    let responseData = res.data.body;
                    setSavedRecipes(responseData);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            setError("Failed to retrieve saved recipes");
        }

        if (error) {
            console.log(error);
        }
    }, [error]);

    const fetchFavoriteRecipes = useCallback(async () => {
        setLoading(true);

        try {
            axiosService
                .get(`${process.env.REACT_APP_API_URL}/recipes/fetchFavorites`)
                .then((res) => {
                    let responseData = res.data.body;
                    setFavoriteRecipes(responseData);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            setError("Failed to retrieve favorite recipes");
        }

        if (error) {
            console.log(error);
        }
    }, [error]);

    const getData = useCallback(async () => {
        fetchFavoriteRecipes();
        fetchChapters();
        fetchSavedRecipes();
    }, [fetchChapters, fetchFavoriteRecipes, fetchSavedRecipes]);

    const handleChapterSwitch = useCallback(
        (index: number) => {
            setShowFavorites(false);
            setSelectedTab(index);

            if (!mdUp) setDrawerOpen(false);

            if (index >= 0) {
                setSaveToChapter(true);
            }

            setCurrentRecipe(null);
            getData();
        },
        [getData, mdUp]
    );

    useEffect(() => {
        if (isGuest) {
            navigate("/login");
            return;
        }
        getData();
    }, [getData, isGuest, navigate]);

    const createNewChapter = useCallback(async () => {
        try {
            await axiosService.post(`${process.env.REACT_APP_API_URL}/chapters/create`, {
                name: newChapterNew,
            });

            const newChapters = (await fetchChapters()) as IChapter[];
            const last = newChapters[newChapters.length - 1];

            if (last) setSelectedTab(newChapters.length - 1);
        } finally {
            setOpenModal(false);
            setDrawerOpen(false);
        }
    }, [fetchChapters, newChapterNew]);

    const handleDeleteChapter = useCallback(() => {
        axiosService
            .post(`${process.env.REACT_APP_API_URL}/chapters/delete`, {
                chapterId: chapters[selectedTab]["_id"],
            })
            .then(() => {
                fetchChapters();
                setSelectedTab(-1);
                setSaveToChapter(false);
            });
    }, [chapters, fetchChapters, selectedTab]);

    const handleFavoritesToggle = useCallback(() => {
        setShowFavorites(true);
        setCurrentRecipe(null);
        setSaveToChapter(false);
        setSelectedTab(-2);
        if (!mdUp) setDrawerOpen(false);
        getData();
    }, [getData, mdUp]);

    const handleSaveToChapterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSaveToChapter(event.target.checked);
    }, []);

    const createNewRecipe = useCallback(() => {
        const chapterId = selectedTab < 0 || chapters.length === 0 ? null : chapters[selectedTab]["_id"];
        const transformedRecipe = addChapterIngredientTransform(editData);

        if (chapterId && saveToChapter) {
            axiosService
                .post(`${process.env.REACT_APP_API_URL}/chapters/addRecipe`, {
                    name: editData.name,
                    description: editData.description,
                    ingredients: transformedRecipe.ingredients,
                    procedure: editData.procedure,
                    tags: { old: [...editData.tags.old], new: [...editData.tags.new] },
                    chapterId: chapterId,
                    included: false,
                })
                .then((res) => getData())
                .finally(() => {
                    setOpenModal(false);
                });
        } else {
            axiosService
                .post(`${process.env.REACT_APP_API_URL}/recipes/create`, {
                    name: editData.name,
                    description: editData.description,
                    ingredients: editData.ingredients,
                    procedure: editData.procedure,
                    tags: editData.tags,
                    chapter: chapterId,
                })
                .then(() => {
                    getData();
                    setOpenModal(false);
                });
        }
    }, [selectedTab, chapters, editData, saveToChapter, getData]);

    const renderLibraryTitle = useMemo(() => {
        if (currentRecipe) return <></>;
        let libraryTitle = "All Recipes";

        if (showFavorites) libraryTitle = "Favorites";
        else if (chapters.length > 0 && selectedTab >= 0) {
            libraryTitle = chapters[selectedTab]["name"];
        }

        return (
            <Box sx={{ width: "100%", maxWidth: "100vw", py: 3, px: 1 }}>
                <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
                    {!mdUp && (
                        <Box
                            sx={{
                                display: "flex",
                                zIndex: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: "50%",
                                mr: 1,
                            }}
                            onClick={() => setDrawerOpen(true)}
                        >
                            <IconButton>
                                <Menu sx={{ color: "white" }} />
                            </IconButton>
                        </Box>
                    )}
                    <TextField
                        variant="outlined"
                        placeholder="Search Recipes..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        fullWidth
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 4,
                                backgroundColor: lighten(theme.palette.text.secondary, 0.9),
                                backdropFilter: "blur(8px)",
                                height: "45px",
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton size="small" aria-label="Search">
                                        <Search color="action" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            endAdornment: query && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setQuery("")} aria-label="Clear">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "baseline",
                        width: "100%",
                        gap: 0.5,
                        p: 1,
                        borderBottom: "1px solid " + theme.palette.primary.main,
                    }}
                >
                    <Typography
                        variant={isXs ? "h5" : "h4"}
                        sx={{
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            flex: 1,
                            mb: 1,
                            pt: 1,
                            pl: 1,
                        }}
                    >
                        {libraryTitle}
                    </Typography>

                    {selectedTab >= 0 && chapters.length && (
                        <Box>
                            <IconButton
                                aria-label="Delete Chapter"
                                onClick={handleDeleteChapter}
                                size="small"
                                sx={{
                                    position: "relative",
                                    bottom: "5px",
                                    color: theme.palette.primary.main,
                                    "&:hover": {
                                        color: "error.main",
                                    },
                                }}
                            >
                                <Delete />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }, [
        chapters,
        currentRecipe,
        handleDeleteChapter,
        isXs,
        query,
        selectedTab,
        showFavorites,
        mdUp,
        theme.palette.primary.main,
        theme.palette.text.secondary,
    ]);

    const renderThumbnailGrid = useMemo(() => {
        return (
            <Box sx={{ pb: 2 }}>
                <ThumbnailGrid
                    items={filteredRecipes}
                    getImage={(item) => (item.image ? item.image.url : "")}
                    getLabel={(item) => item.name}
                    onClick={(item) => setCurrentRecipe(item)}
                />
            </Box>
        );
    }, [filteredRecipes]);

    const renderLibraryRecipe = useMemo(() => {
        if (currentRecipe === null) return;

        let props = chapters[selectedTab]
            ? chapters[selectedTab]["recipes"].length > 0
                ? { setCurrentRecipe: setCurrentRecipe, recipe: currentRecipe, chapter: chapters[selectedTab], getData: getData }
                : { setCurrentRecipe: setCurrentRecipe, recipe: currentRecipe, getData: getData }
            : { setCurrentRecipe: setCurrentRecipe, recipe: currentRecipe, getData: getData };

        return <LibraryRecipe {...props} />;
    }, [chapters, currentRecipe, getData, selectedTab]);

    const recipeDrawer = useMemo(
        () => (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    width: "250px",
                    p: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        maxWidth: "100%",
                        flexDirection: "column",
                        mb: 2,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CustomButton
                        label="New Recipe"
                        sx={{ color: "white", width: "100%" }}
                        onClick={() => {
                            dispatch(
                                resetEditData({
                                    name: "",
                                    description: "",
                                    ingredients: [],
                                    procedure: [],
                                    tags: { old: [], new: [] },
                                })
                            );
                            setDialogMode("recipe");
                            setOpenModal(true);
                        }}
                    />
                </Box>
                <Divider />
                {/* <Box
                    onClick={handleFavoritesToggle}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        my: 1,
                        cursor: "pointer",
                        borderRadius: 5,
                        userSelect: "none",
                        backgroundColor: showFavorites ? theme.palette.secondary.main : "default",
                    }}
                >
                    <Typography variant="h6" sx={{ px: 1 }}>
                        Favorites
                    </Typography>
                </Box> */}
                <Divider />
                <Box
                    sx={{ display: "flex", alignItems: "center", my: 1 }}
                    onClick={() => {
                        setDialogMode("chapter");
                        setOpenModal(true);
                    }}
                >
                    <Typography variant="h6" sx={{ px: 1, flexGrow: 1, userSelect: "none", cursor: "pointer" }}>
                        Chapters
                    </Typography>
                    <IconButton sx={{ position: "relative", top: "2px", padding: 0 }}>
                        <AddBox sx={{ fontSize: "32px" }} />
                    </IconButton>
                </Box>
                <Divider />
                <Box sx={{ width: "100%", maxWidth: "100%" }}>
                    <Box>
                        <ListItemButton
                            selected={selectedTab === -1}
                            onClick={() => handleChapterSwitch(-1)}
                            sx={{
                                width: "100%",
                                textAlign: "center",
                                maxWidth: "100%",
                                borderRadius: "20px",
                                margin: "5px 0px 5px 0px",
                            }}
                        >
                            <Typography>All Recipes</Typography>
                        </ListItemButton>
                    </Box>
                    <Box>
                        <ListItemButton
                            selected={selectedTab === -2}
                            onClick={handleFavoritesToggle}
                            sx={{
                                width: "100%",
                                textAlign: "center",
                                maxWidth: "100%",
                                borderRadius: "20px",
                                margin: "5px 0px 5px 0px",
                            }}
                        >
                            <Typography>Favorites</Typography>
                        </ListItemButton>
                    </Box>
                    <Sidebar tabs={sidebarTabs} selectedTab={selectedTab} onSelectTab={handleChapterSwitch} />
                </Box>
            </Box>
        ),
        [dispatch, handleChapterSwitch, handleFavoritesToggle, selectedTab, sidebarTabs]
    );

    if (isGuest) {
        return <Box></Box>;
    }

    if (loading)
        return (
            <Box
                sx={{
                    height: "calc(100dvh - 200px)",
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <LoadingWithTip />
            </Box>
        );

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                {mdUp && recipeDrawer}
                <Box sx={{ width: "100%", maxWidth: 1600 }}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {renderLibraryTitle}

                        {currentRecipe === null ? renderThumbnailGrid : renderLibraryRecipe}
                    </Box>
                </Box>
            </Box>
            {!mdUp && (
                <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                    {recipeDrawer}
                </Drawer>
            )}

            <Dialog
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                }}
                maxWidth={dialogMode === "chapter" ? "xs" : "md"}
                fullWidth
            >
                {dialogMode === "chapter" && (
                    <>
                        <DialogTitle sx={{ textAlign: "center" }}>Create Chapter</DialogTitle>
                        <DialogContent dividers>
                            <TextField
                                autoFocus
                                onChange={(e) => setNewChapterName(e.target.value)}
                                label="Chapter Name"
                                variant="standard"
                                sx={{ width: "100%" }}
                            />
                            <CustomButton onClick={createNewChapter} label="Create Chapter" sx={{ color: "white", width: "100%", mt: 3 }} />
                        </DialogContent>
                    </>
                )}

                {dialogMode === "recipe" && (
                    <DialogContent
                        dividers
                        sx={{
                            maxWidth: { xs: "100vw", sm: "90vw" },
                            maxHeight: { xs: "100dvh", sm: "90dvh" },
                            overflow: "auto",
                            ...scrollbarCss,
                            px: 2,
                            pt: 1,
                            pb: 0,
                        }}
                    >
                        <Box sx={{ position: "relative" }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Typography variant="h3" sx={{ flexGrow: 1, fontSize: { xs: 24, sm: 28 } }}>
                                    Create Recipe
                                </Typography>
                                <IconButton
                                    onClick={() => {
                                        dispatch(
                                            resetEditData({
                                                name: "",
                                                description: "",
                                                ingredients: [],
                                                procedure: [],
                                                tags: { old: [], new: [] },
                                            })
                                        );
                                        setOpenModal(false);
                                    }}
                                >
                                    <Close />
                                </IconButton>
                            </Box>

                            <Divider />

                            {selectedTab >= 0 && (
                                <Box sx={{ display: "flex", alignItems: "center", pl: 1 }}>
                                    <Typography>Add recipe to current chapter ({chapters[selectedTab].name})?</Typography>
                                    <Checkbox checked={saveToChapter} onChange={handleSaveToChapterChange} />
                                </Box>
                            )}

                            <EditRecipe editData={editData} submit={createNewRecipe} />
                        </Box>
                    </DialogContent>
                )}
            </Dialog>
        </ThemeProvider>
    );
};

export default Library;
